import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Trash2, Edit2, ClipboardCheck, X, Link2, Link2Off, Copy as CopyIcon, ExternalLink, ScanLine, GitBranch, Archive, Printer, History } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';

const FRONTEND_URL = (typeof window !== 'undefined' ? window.location.origin : '');

const ITEM_TYPES = [
  { value: 'checkbox', label: 'Pass / Fail Check' },
  { value: 'measurement', label: 'Measurement' },
  { value: 'text', label: 'Text Note' },
  { value: 'signature', label: 'Signature' },
];

const APPLIES_TO = [
  { value: 'any', label: 'Any production' },
  { value: 'part_assembly', label: 'Part Assembly only' },
  { value: 'final_product', label: 'Final Product only' },
];

const blankTemplate = () => ({
  template_name: '',
  description: '',
  applies_to: 'any',
  active: true,
  items: [],
});

const blankItem = () => ({
  label: '',
  item_type: 'checkbox',
  description: '',
  target: '',
  min_value: '',
  max_value: '',
  unit: '',
  required: true,
});

export default function QCTemplatesPage() {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [showDeprecated, setShowDeprecated] = useState(false);
  const [chainDialog, setChainDialog] = useState(null); // { template, revisions }
  const [deprecateDialog, setDeprecateDialog] = useState(null); // template
  const [deprecateReason, setDeprecateReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankTemplate());

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = showDeprecated ? '?include_deprecated=true&include_old_revisions=true' : '';
      const res = await api().get(`/qc-templates${params}`);
      setTemplates(res.data);
    } catch {
      toast.error('Failed to load QC templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, [showDeprecated]);

  const newRevision = async (tpl) => {
    if (!window.confirm(`Create a new revision of "${tpl.template_name}"? The current revision (v${tpl.revision_number || 1}) will be preserved read-only for historical submissions.`)) return;
    try {
      const r = await api().post(`/qc-templates/${tpl.id}/new-revision`, {});
      toast.success(`Revision v${r.data.revision_number} created — opening for edit`);
      fetchTemplates();
      // Re-fetch and open the new revision in the edit dialog
      const tplR = await api().get(`/qc-templates/${r.data.id}`);
      openEdit(tplR.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deprecate = async () => {
    if (!deprecateDialog) return;
    try {
      await api().post(`/qc-templates/${deprecateDialog.id}/deprecate`, { reason: deprecateReason });
      toast.success('Template deprecated');
      setDeprecateDialog(null); setDeprecateReason('');
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const showChain = async (tpl) => {
    try {
      const r = await api().get(`/qc-templates/${tpl.id}/revision-chain`);
      setChainDialog({ template: tpl, revisions: r.data.revisions || [] });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // iter68 — branded printable QC template
  const printTemplate = async (tpl) => {
    const { openBrandedPrintWindow, PrintHelpers: H } = await import('../utils/brandedPrint');
    const cols = [
      { key: 'idx', label: '#', mono: true, fmt: (_, r, i) => String((r.__idx || 0) + 1) },
      { key: 'label', label: 'Inspection Item' },
      { key: 'item_type', label: 'Type' },
      { key: 'target', label: 'Target', mono: true },
      { key: 'min_value', label: 'Min', mono: true },
      { key: 'max_value', label: 'Max', mono: true },
      { key: 'unit', label: 'UoM' },
      { key: 'required', label: 'Req?', fmt: (v) => v ? 'YES' : 'no' },
      { key: 'sig', label: 'Pass / Fail / Reading', fmt: () => '<span style="display:inline-block; width:120pt; border-bottom:1px solid #999; height:12pt;"></span>' },
    ];
    const rows = (tpl.items || []).map((it, idx) => ({ ...it, __idx: idx }));
    const meta = H.kvGrid([
      ['Template', tpl.template_name],
      ['Revision', `v${tpl.revision_number || 1}${tpl.is_deprecated ? ' · DEPRECATED' : tpl.is_latest_revision === false ? ' · superseded' : ' · current'}`],
      ['Applies To', String(tpl.applies_to || 'any').replace('_', ' ')],
      ['Items', String((tpl.items || []).length)],
    ]);
    await openBrandedPrintWindow({
      title: 'QC INSPECTION SHEET',
      subtitle: `${tpl.template_name} · v${tpl.revision_number || 1}`,
      orientation: 'portrait',
      statusBadge: H.statusBadge(tpl.is_deprecated ? 'DEPRECATED' : tpl.is_latest_revision === false ? 'Superseded' : 'Active', tpl.is_deprecated ? '#dc2626' : tpl.is_latest_revision === false ? '#6b7280' : '#16a34a'),
      body: `${meta}${tpl.description ? H.callout(H.escape(tpl.description)) : ''}${H.sectionTitle('Inspection Items')}${H.table(cols, rows)}${H.sectionTitle('Sign-off')}${H.signoffLines('Inspector Signature', 'Date')}`,
      footerNote: 'QC Sheet · Controlled Document',
    });
  };

  // iter68 — branded printable QC submission certificate
  const printSubmission = async (sub) => {
    const { openBrandedPrintWindow, PrintHelpers: H } = await import('../utils/brandedPrint');
    try {
      const r = await api().get(`/qc-submissions/${sub.id}`);
      const full = r.data;
      const tpl = full.template || {};
      const meta = H.kvGrid([
        ['Submission ID', sub.id.slice(0, 8) + '…'],
        ['Template', `${sub.template_name || tpl.template_name} · v${tpl.revision_number || 1}`],
        ['Serial', sub.serial_number || '—'],
        ['Inspector', sub.submitted_by_name || '—'],
        ['Submitted', sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : '—'],
        ['Overall', sub.overall_status?.toUpperCase() || '—'],
      ]);
      const ansCols = [
        { key: 'label', label: 'Check' },
        { key: 'item_type', label: 'Type' },
        { key: 'pass_fail', label: 'P/F', fmt: (v) => v === true ? '✓ PASS' : v === false ? '✗ FAIL' : '—' },
        { key: 'measurement', label: 'Measurement', mono: true, fmt: (v) => v != null ? String(v) : '—' },
        { key: 'text_value', label: 'Notes' },
      ];
      const url = sub.public_token && !sub.public_token_revoked
        ? `${FRONTEND_URL}/public/qc-submissions/${sub.public_token}` : '';
      await openBrandedPrintWindow({
        title: 'QC INSPECTION RECORD',
        subtitle: `${sub.template_name} · ${sub.serial_number || '(no serial)'}`,
        orientation: 'portrait',
        qrUrl: url || undefined,
        statusBadge: H.statusBadge(sub.overall_status || '—', sub.overall_status === 'pass' ? '#16a34a' : sub.overall_status === 'fail' ? '#dc2626' : '#6b7280'),
        body: `${meta}${H.sectionTitle('Inspection Answers')}${H.table(ansCols, full.answers || [])}${sub.inspector_notes ? `${H.sectionTitle('Inspector Notes')}${H.callout(H.escape(sub.inspector_notes))}` : ''}${H.signoffLines(`Inspector: ${sub.submitted_by_name || ''}`, sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : '')}`,
        footerNote: 'QC Inspection Record · Controlled Document',
      });
    } catch (e) {
      toast.error('Failed to load submission for print');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(blankTemplate());
    setDialogOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({
      template_name: tpl.template_name || '',
      description: tpl.description || '',
      applies_to: tpl.applies_to || 'any',
      active: tpl.active !== false,
      items: (tpl.items || []).map(it => ({
        ...blankItem(),
        ...it,
        min_value: it.min_value ?? '',
        max_value: it.max_value ?? '',
      })),
    });
    setDialogOpen(true);
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, blankItem()] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, patch) => setForm(f => ({
    ...f,
    items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
  }));

  const save = async () => {
    if (!form.template_name.trim()) { toast.error('Template name is required'); return; }
    if (form.items.length === 0) { toast.error('Add at least one check item'); return; }
    const payload = {
      ...form,
      items: form.items.map(it => ({
        ...it,
        min_value: it.min_value === '' || it.min_value === null ? null : Number(it.min_value),
        max_value: it.max_value === '' || it.max_value === null ? null : Number(it.max_value),
      })),
    };
    try {
      if (editing) {
        await api().put(`/qc-templates/${editing.id}`, payload);
        toast.success('QC template updated');
      } else {
        await api().post('/qc-templates', payload);
        toast.success('QC template created');
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this QC template? Existing submissions are preserved.')) return;
    try {
      await api().delete(`/qc-templates/${id}`);
      toast.success('Deleted');
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // ===== iter37 — recent QC submissions + public link issue/revoke =====
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [qrPreview, setQrPreview] = useState(null); // { url, submission }

  const fetchSubmissions = async () => {
    setSubmissionsLoading(true);
    try {
      const res = await api().get('/qc-submissions');
      setSubmissions(res.data || []);
    } catch {
      // silent — page is mainly for templates
    } finally {
      setSubmissionsLoading(false);
    }
  };

  useEffect(() => { fetchSubmissions(); }, []);

  const issueQcPublic = async (sub) => {
    try {
      await api().post(`/qc-submissions/${sub.id}/public-link`);
      toast.success('Public QC link issued');
      fetchSubmissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to issue link');
    }
  };
  const revokeQcPublic = async (sub) => {
    if (!window.confirm('Revoke the public QC link? Any printed QR for this submission will stop working.')) return;
    try {
      await api().delete(`/qc-submissions/${sub.id}/public-link`);
      toast.success('Public QC link revoked');
      fetchSubmissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke link');
    }
  };
  const copyQcUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Public URL copied');
    } catch { toast.error('Copy failed'); }
  };
  const submissionPublicUrl = (sub) =>
    sub.public_token && !sub.public_token_revoked ? `${FRONTEND_URL}/public/qc-submissions/${sub.public_token}` : '';

  return (
    <div className="space-y-6 animate-slideIn" data-testid="qc-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">QC Sheet Templates</h1>
          <p className="text-slate-500 text-sm">Author the inspection forms used in the Production Queue → Quality Check tab.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={showDeprecated} onChange={(e) => setShowDeprecated(e.target.checked)} className="accent-orange-500" data-testid="qc-show-deprecated-toggle" />
            <span>Show deprecated & old revisions</span>
          </label>
          <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" onClick={openCreate} data-testid="qc-template-add-btn">
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
        </div>
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <CardTitle className="text-base">Library ({templates.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48"><div className="spinner" /></div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <ClipboardCheck className="w-12 h-12 mb-3 text-slate-300" />
              <p>No QC templates yet</p>
              <Button variant="link" onClick={openCreate} className="text-orange-600">Create your first template</Button>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Applies To</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map(tpl => (
                  <TableRow key={tpl.id} data-testid={`qc-template-row-${tpl.id}`} className={tpl.is_deprecated ? 'opacity-50' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{tpl.template_name}</span>
                        <Badge variant="outline" className="text-[10px] font-mono" data-testid={`qc-rev-badge-${tpl.id}`}>v{tpl.revision_number || 1}</Badge>
                        {tpl.is_deprecated && <Badge className="bg-red-600 text-[10px]">DEPRECATED</Badge>}
                        {!tpl.is_deprecated && tpl.is_latest_revision === false && <Badge className="bg-slate-400 text-[10px]">SUPERSEDED</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm capitalize">{(tpl.applies_to || 'any').replace('_', ' ')}</TableCell>
                    <TableCell>{(tpl.items || []).length}</TableCell>
                    <TableCell>
                      <span className={`status-badge ${tpl.active ? 'status-released' : 'status-obsolete'}`}>{tpl.active ? 'Active' : 'Inactive'}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => printTemplate(tpl)} data-testid={`qc-template-print-${tpl.id}`} title="Print branded">
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => showChain(tpl)} data-testid={`qc-template-history-${tpl.id}`} title="Revision history">
                          <History className="w-4 h-4" />
                        </Button>
                        {!tpl.is_deprecated && (tpl.is_latest_revision !== false) && (
                          <Button size="sm" variant="ghost" onClick={() => newRevision(tpl)} data-testid={`qc-template-revise-${tpl.id}`} title="Create new revision">
                            <GitBranch className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEdit(tpl)} data-testid={`qc-template-edit-${tpl.id}`} title={tpl.is_latest_revision === false ? 'Old revisions are read-only — use New Revision' : 'Edit'}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {!tpl.is_deprecated && (
                          <Button size="sm" variant="ghost" onClick={() => setDeprecateDialog(tpl)} className="text-amber-700 hover:bg-amber-50" data-testid={`qc-template-deprecate-${tpl.id}`} title="Deprecate">
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(tpl.id)} data-testid={`qc-template-delete-${tpl.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'New'} QC Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={form.template_name}
                  onChange={e => setForm({ ...form, template_name: e.target.value })}
                  placeholder="e.g., HVAC Final Inspection"
                  data-testid="qc-template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Applies To</Label>
                <Select value={form.applies_to} onValueChange={v => setForm({ ...form, applies_to: v })}>
                  <SelectTrigger data-testid="qc-template-applies-to-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {APPLIES_TO.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What this inspection covers..." rows={2} />
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2">
              <Label>Template Active</Label>
              <Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} data-testid="qc-template-active-switch" />
            </div>

            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between">
                <Label className="text-base">Inspection Items ({form.items.length})</Label>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="qc-template-add-item-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>
              {form.items.length === 0 && (
                <p className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-sm p-4 text-center">Click "Add Item" to start building the form.</p>
              )}
              {form.items.map((it, idx) => (
                <div key={idx} className="border border-slate-200 rounded-sm p-3 space-y-2" data-testid={`qc-template-item-${idx}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="grid grid-cols-12 gap-2 flex-1">
                      <div className="col-span-7">
                        <Label className="text-xs">Item Label *</Label>
                        <Input value={it.label} onChange={e => updateItem(idx, { label: e.target.value })} placeholder="e.g., Tighten chassis bolts to 12 N·m" data-testid={`qc-item-${idx}-label`} />
                      </div>
                      <div className="col-span-5">
                        <Label className="text-xs">Type</Label>
                        <Select value={it.item_type} onValueChange={v => updateItem(idx, { item_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ITEM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeItem(idx)} className="text-red-600 mt-5">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {it.item_type === 'measurement' && (
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs">Target</Label>
                        <Input value={it.target} onChange={e => updateItem(idx, { target: e.target.value })} placeholder="12.0" />
                      </div>
                      <div>
                        <Label className="text-xs">Min</Label>
                        <Input type="number" value={it.min_value} onChange={e => updateItem(idx, { min_value: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-xs">Max</Label>
                        <Input type="number" value={it.max_value} onChange={e => updateItem(idx, { max_value: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-xs">Unit</Label>
                        <Input value={it.unit} onChange={e => updateItem(idx, { unit: e.target.value })} placeholder="N·m, mm…" />
                      </div>
                    </div>
                  )}
                  {it.item_type !== 'signature' && (
                    <div>
                      <Label className="text-xs">Helper / instructions</Label>
                      <Input value={it.description} onChange={e => updateItem(idx, { description: e.target.value })} placeholder="Optional guidance shown to inspector" />
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Switch checked={it.required} onCheckedChange={v => updateItem(idx, { required: v })} />
                    <span>Required</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="qc-template-save-btn">
              {editing ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter37 — Recent QC Submissions + Public Read-Only Links */}
      <Card className="industrial-card" data-testid="qc-submissions-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ScanLine className="w-4 h-4 text-orange-600" /> Recent QC Submissions ({submissions.length})
          </CardTitle>
          <Button size="sm" variant="outline" onClick={fetchSubmissions} data-testid="qc-submissions-refresh-btn">Refresh</Button>
        </CardHeader>
        <CardContent className="p-0">
          {submissionsLoading ? (
            <div className="flex items-center justify-center h-32"><div className="spinner" /></div>
          ) : submissions.length === 0 ? (
            <p className="text-sm text-slate-500 p-6 text-center">No QC submissions yet. Run a QC sheet from Production Queue → Quality Check.</p>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Serial</TableHead>
                  <TableHead>Inspector</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Public Link</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.slice(0, 50).map(s => {
                  const url = submissionPublicUrl(s);
                  const active = !!url;
                  return (
                    <TableRow key={s.id} data-testid={`qc-submission-row-${s.id}`}>
                      <TableCell className="text-xs">{s.submitted_at ? new Date(s.submitted_at).toLocaleString() : '—'}</TableCell>
                      <TableCell className="text-xs">{s.template_name || '—'}</TableCell>
                      <TableCell className="text-xs font-mono">{s.serial_number || '—'}</TableCell>
                      <TableCell className="text-xs">{s.submitted_by_name || '—'}</TableCell>
                      <TableCell>
                        <Badge className={s.overall_status === 'pass' ? 'bg-emerald-600' : s.overall_status === 'fail' ? 'bg-red-600' : 'bg-slate-400'}>
                          {(s.overall_status || '—').toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {active ? (
                          <Badge className="bg-emerald-600 text-[10px]" data-testid={`qc-pub-status-${s.id}`}>ACTIVE</Badge>
                        ) : (
                          <Badge className="bg-slate-400 text-[10px]" data-testid={`qc-pub-status-${s.id}`}>NOT ISSUED</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => printSubmission(s)} data-testid={`qc-sub-print-${s.id}`} title="Print branded record">
                            <Printer className="w-4 h-4" />
                          </Button>
                          {active ? (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => setQrPreview({ url, submission: s })} data-testid={`qc-pub-qr-${s.id}`} title="Show QR">
                                <ScanLine className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => copyQcUrl(url)} data-testid={`qc-pub-copy-${s.id}`} title="Copy URL">
                                <CopyIcon className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => window.open(url, '_blank')} data-testid={`qc-pub-open-${s.id}`} title="Open public page">
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => revokeQcPublic(s)} data-testid={`qc-pub-revoke-${s.id}`} title="Revoke">
                                <Link2Off className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => issueQcPublic(s)} data-testid={`qc-pub-issue-${s.id}`}>
                              <Link2 className="w-4 h-4 mr-1" /> Issue
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* QR preview dialog */}
      <Dialog open={!!qrPreview} onOpenChange={(o) => !o && setQrPreview(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-sm">Public QC Submission QR</DialogTitle></DialogHeader>
          {qrPreview && (
            <div className="space-y-3 text-center">
              <div className="bg-white border border-slate-200 rounded-sm p-3 inline-block mx-auto">
                <QRCodeSVG value={qrPreview.url} size={220} level="M" data-testid="qc-pub-qr-preview" />
              </div>
              <div className="text-xs text-slate-500 space-y-0.5">
                <p className="font-medium">{qrPreview.submission.template_name || 'QC Submission'}</p>
                <p className="font-mono text-[10px]">{qrPreview.submission.serial_number || '—'}</p>
                <p className="font-mono text-[9px] break-all">{qrPreview.url}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => copyQcUrl(qrPreview.url)} className="flex-1"><CopyIcon className="w-3 h-3 mr-1" /> Copy</Button>
                <Button size="sm" onClick={() => window.open(qrPreview.url, '_blank')} className="flex-1 bg-slate-900"><ExternalLink className="w-3 h-3 mr-1" /> Open</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* iter68 — Revision Chain dialog */}
      <Dialog open={!!chainDialog} onOpenChange={(o) => !o && setChainDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><History className="w-4 h-4 text-blue-600" /> Revision History — {chainDialog?.template?.template_name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <Table className="data-table">
              <TableHeader><TableRow><TableHead>Rev</TableHead><TableHead>Items</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
              <TableBody>
                {(chainDialog?.revisions || []).map(r => (
                  <TableRow key={r.id} data-testid={`qc-chain-row-${r.id}`}>
                    <TableCell><Badge variant="outline" className="font-mono">v{r.revision_number || 1}</Badge></TableCell>
                    <TableCell>{(r.items || []).length}</TableCell>
                    <TableCell>
                      {r.is_deprecated ? <Badge className="bg-red-600 text-[10px]">DEPRECATED</Badge>
                        : r.is_latest_revision !== false ? <Badge className="bg-emerald-600 text-[10px]">CURRENT</Badge>
                        : <Badge className="bg-slate-400 text-[10px]">SUPERSEDED</Badge>}
                    </TableCell>
                    <TableCell className="text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter68 — Deprecate dialog */}
      <Dialog open={!!deprecateDialog} onOpenChange={(o) => { if (!o) { setDeprecateDialog(null); setDeprecateReason(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Archive className="w-4 h-4 text-amber-600" /> Deprecate QC Template</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">Deprecating <strong>{deprecateDialog?.template_name}</strong> (v{deprecateDialog?.revision_number || 1}) hides it from the picker for new inspections. Existing submissions remain intact and printable.</p>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea value={deprecateReason} onChange={(e) => setDeprecateReason(e.target.value)} placeholder="e.g., Replaced by updated checklist after audit finding" rows={3} data-testid="qc-deprecate-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeprecateDialog(null); setDeprecateReason(''); }}>Cancel</Button>
            <Button onClick={deprecate} className="bg-amber-600 hover:bg-amber-700" data-testid="qc-deprecate-confirm-btn"><Archive className="w-4 h-4 mr-1" /> Deprecate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
